import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, BarChart3, FileCheck, Bell, Trophy, CheckCircle2, Download, Briefcase, Trash2, Plus, ShieldCheck, XCircle, MessageSquareQuote, Star, Youtube, Save, Phone, PhoneCall, Video, LineChart, Mail } from "lucide-react";
import api, { API } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ProctoringSessionsPanel from "@/components/admin/ProctoringSessionsPanel";
import PageAnalyticsPanel from "@/components/admin/PageAnalyticsPanel";
import CrmHubPanel from "@/components/admin/crm/CrmHubPanel";

const TRACK_BADGE = {
  data_cloud: { label: "Data Cloud", classes: "bg-indigo-400/10 text-indigo-300 border-indigo-400/30" },
  sfmc: { label: "SFMC", classes: "bg-teal-400/10 text-teal-300 border-teal-400/30" },
  mc_next: { label: "MC Next", classes: "bg-fuchsia-400/10 text-fuchsia-300 border-fuchsia-400/30" },
};

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [leads, setLeads] = useState([]);
  const [notifTitle, setNotifTitle] = useState("");
  const [notifMessage, setNotifMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [newJob, setNewJob] = useState({ title: "", company: "", location: "", job_type: "Full-time", description: "", apply_url: "", track: "data_cloud" });
  const [creatingJob, setCreatingJob] = useState(false);
  const [videoDrafts, setVideoDrafts] = useState({});
  const [savingVideoDay, setSavingVideoDay] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/challenges").then((r) => setChallenges(r.data));
    api.get("/admin/submissions").then((r) => setSubmissions(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/jobs").then((r) => setJobs(r.data));
    api.get("/admin/recruiters").then((r) => setRecruiters(r.data));
    api.get("/admin/testimonials").then((r) => setTestimonials(r.data));
    api.get("/admin/leads").then((r) => setLeads(r.data));
  }, []);

  const saveVideoUrl = async (day) => {
    setSavingVideoDay(day);
    try {
      const video_url = (videoDrafts[day] ?? "").trim();
      const { data } = await api.put(`/admin/challenges/${day}`, { video_url });
      setChallenges((prev) => prev.map((c) => (c.day === day ? data : c)));
      toast.success(`Day ${day} video updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update video");
    } finally {
      setSavingVideoDay(null);
    }
  };

  const approveTestimonial = async (id) => {
    await api.post(`/admin/testimonials/${id}/approve`);
    setTestimonials((prev) => prev.map((t) => (t.id === id ? { ...t, status: "approved" } : t)));
    toast.success("Testimonial approved — now live on the landing page");
  };

  const rejectTestimonial = async (id) => {
    await api.post(`/admin/testimonials/${id}/reject`);
    setTestimonials((prev) => prev.map((t) => (t.id === id ? { ...t, status: "rejected" } : t)));
    toast.success("Testimonial rejected");
  };

  const approveRecruiter = async (userId) => {
    await api.post(`/admin/recruiters/${userId}/approve`);
    setRecruiters((prev) => prev.map((r) => (r.user_id === userId ? { ...r, recruiter_status: "approved" } : r)));
    toast.success("Recruiter approved");
  };

  const rejectRecruiter = async (userId) => {
    await api.post(`/admin/recruiters/${userId}/reject`);
    setRecruiters((prev) => prev.map((r) => (r.user_id === userId ? { ...r, recruiter_status: "rejected" } : r)));
    toast.success("Recruiter rejected");
  };

  const approveSubmission = async (id) => {
    await api.put(`/admin/submissions/${id}/approve`);
    setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, status: "approved" } : s)));
    toast.success("Submission approved");
  };

  const sendNotification = async () => {
    if (!notifTitle.trim() || !notifMessage.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications", { title: notifTitle, message: notifMessage });
      toast.success(`Sent to ${data.sent_to} users`);
      setNotifTitle("");
      setNotifMessage("");
    } finally {
      setSending(false);
    }
  };

  const createJob = async () => {
    if (!newJob.title.trim() || !newJob.company.trim() || !newJob.apply_url.trim()) {
      toast.error("Title, company, and apply URL are required");
      return;
    }
    setCreatingJob(true);
    try {
      const { data } = await api.post("/admin/jobs", newJob);
      setJobs((prev) => [data, ...prev]);
      setNewJob({ title: "", company: "", location: "", job_type: "Full-time", description: "", apply_url: "", track: "data_cloud" });
      toast.success("Job posted");
    } finally {
      setCreatingJob(false);
    }
  };

  const deleteJob = async (id) => {
    await api.delete(`/admin/jobs/${id}`);
    setJobs((prev) => prev.filter((j) => j.id !== id));
    toast.success("Job removed");
  };

  const updateLeadStatus = async (id, status) => {
    await api.put(`/admin/leads/${id}/status`, { status });
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    toast.success(`Lead marked as ${status}`);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold text-white">Admin Panel</h1>
        <div className="flex items-center gap-2">
          <a href={`${API}/admin/export/users`} data-testid="export-users-header-btn" className="flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white transition-colors">
            <Download className="w-3.5 h-3.5" /> Export Users CSV
          </a>
          <a href={`${API}/admin/export/certificates`} data-testid="export-certificates-btn" className="flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors">
            <Download className="w-3.5 h-3.5" /> Export Certificates CSV
          </a>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            ["Total Users", stats.total_users, Users],
            ["Completions", stats.total_completions, CheckCircle2],
            ["Certificates", stats.total_certificates, Trophy],
            ["Avg Quiz Score", `${stats.avg_quiz_score}%`, BarChart3],
            ["Data Cloud Enrolled", stats.data_cloud_enrolled, Users],
            ["SFMC Enrolled", stats.sfmc_enrolled, Users],
            ["MC Next Enrolled", stats.mc_next_enrolled, Users],
            ["Admin + Dev Enrolled", stats.admin_dev_enrolled, Users],
            ["ServiceNow Enrolled", stats.servicenow_enrolled, Users],
          ].map(([label, val, Icon]) => (
            <div key={label} className="glass-panel rounded-xl p-4" data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
              <Icon className="w-4 h-4 text-indigo-400 mb-2" />
              <p className="font-heading text-2xl font-bold text-white">{val}</p>
              <p className="text-xs text-slate-400">{label}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="challenges">
        <TabsList className="bg-white/5 p-1.5 mb-6">
          <TabsTrigger value="challenges" data-testid="admin-tab-challenges" className="data-[state=active]:bg-primary data-[state=active]:text-white">Challenges</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="admin-tab-submissions" className="data-[state=active]:bg-primary data-[state=active]:text-white">Submissions</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-primary data-[state=active]:text-white">Users</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="admin-tab-notifications" className="data-[state=active]:bg-primary data-[state=active]:text-white">Notifications</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="admin-tab-jobs" className="data-[state=active]:bg-primary data-[state=active]:text-white">Jobs</TabsTrigger>
          <TabsTrigger value="recruiters" data-testid="admin-tab-recruiters" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Recruiters {recruiters.filter((r) => r.recruiter_status === "pending").length > 0 && (
              <span className="ml-1.5 bg-amber-400 text-black text-[10px] font-bold rounded-full w-4 h-4 inline-flex items-center justify-center">{recruiters.filter((r) => r.recruiter_status === "pending").length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Testimonials {testimonials.filter((t) => t.status === "pending").length > 0 && (
              <span className="ml-1.5 bg-amber-400 text-black text-[10px] font-bold rounded-full w-4 h-4 inline-flex items-center justify-center">{testimonials.filter((t) => t.status === "pending").length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="leads" data-testid="admin-tab-leads" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Leads {leads.filter((l) => l.status === "new").length > 0 && (
              <span className="ml-1.5 bg-amber-400 text-black text-[10px] font-bold rounded-full w-4 h-4 inline-flex items-center justify-center">{leads.filter((l) => l.status === "new").length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="proctoring" data-testid="admin-tab-proctoring" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Video className="w-3.5 h-3.5 mr-1.5 inline" /> Proctoring
          </TabsTrigger>
          <TabsTrigger value="page-analytics" data-testid="admin-tab-page-analytics" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <LineChart className="w-3.5 h-3.5 mr-1.5 inline" /> Page Analytics
          </TabsTrigger>
          <TabsTrigger value="crm" data-testid="admin-tab-crm" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            <Mail className="w-3.5 h-3.5 mr-1.5 inline" /> CRM
          </TabsTrigger>
        </TabsList>

        <TabsContent value="challenges" data-testid="admin-content-challenges">
          <div className="glass-panel rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-white/10">
                  <th className="p-3">Day</th><th className="p-3">Title</th><th className="p-3">Difficulty</th><th className="p-3">XP</th><th className="p-3">YouTube Video ID / URL</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {challenges.map((c) => (
                  <tr key={c.day} className="border-b border-white/5 last:border-0" data-testid={`admin-challenge-row-${c.day}`}>
                    <td className="p-3 text-slate-400">{c.day}</td>
                    <td className="p-3 text-white">{c.title}</td>
                    <td className="p-3 text-slate-400">{c.difficulty}</td>
                    <td className="p-3 text-[#FBBF24]">{c.xp_reward}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1.5">
                        <Youtube className="w-3.5 h-3.5 text-red-400 shrink-0" />
                        <input
                          defaultValue={c.video_url || ""}
                          onChange={(e) => setVideoDrafts((d) => ({ ...d, [c.day]: e.target.value }))}
                          placeholder="Video ID or URL"
                          data-testid={`admin-video-url-input-${c.day}`}
                          className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white w-40 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        />
                      </div>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => saveVideoUrl(c.day)}
                        disabled={savingVideoDay === c.day}
                        data-testid={`admin-video-save-btn-${c.day}`}
                        className="flex items-center gap-1 text-xs font-semibold bg-white/5 hover:bg-white/10 text-white px-2.5 py-1.5 rounded-md disabled:opacity-50"
                      >
                        <Save className="w-3 h-3" /> Save
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="submissions" data-testid="admin-content-submissions">
          <div className="flex justify-end mb-3">
            <a href={`${API}/admin/export/submissions`} data-testid="export-submissions-btn" className="flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </a>
          </div>
          <div className="space-y-3">
            {submissions.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No submissions yet.</p>}
            {submissions.map((s) => (
              <div key={s.id} className="glass-panel rounded-xl p-4" data-testid={`admin-submission-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-white">{s.user_name} — Day {s.day}</p>
                    <p className="text-xs text-slate-400 mt-0.5">AI Score: {s.ai_score}/100 · Status: {s.status}</p>
                  </div>
                  {s.status !== "approved" && (
                    <button onClick={() => approveSubmission(s.id)} data-testid={`admin-approve-btn-${s.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30">
                      Approve
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-2 line-clamp-2">{s.text_content}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users" data-testid="admin-content-users">
          <div className="flex justify-end mb-3">
            <a href={`${API}/admin/export/users`} data-testid="export-users-btn" className="flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </a>
          </div>
          <div className="glass-panel rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-white/10">
                  <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">XP</th><th className="p-3">Streak</th><th className="p-3">Role</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-white/5 last:border-0" data-testid={`admin-user-row-${u.user_id}`}>
                    <td className="p-3 text-white">{u.name}</td>
                    <td className="p-3 text-slate-400">{u.email}</td>
                    <td className="p-3 text-[#FBBF24]">{u.xp}</td>
                    <td className="p-3 text-indigo-400">{u.streak_current}</td>
                    <td className="p-3 text-slate-400">{u.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="notifications" data-testid="admin-content-notifications">
          <div className="glass-panel rounded-2xl p-6 max-w-lg">
            <div className="flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-indigo-400" /><h3 className="font-heading font-semibold text-white">Broadcast Notification</h3></div>
            <input value={notifTitle} onChange={(e) => setNotifTitle(e.target.value)} placeholder="Title" data-testid="notification-title-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white mb-3 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            <textarea value={notifMessage} onChange={(e) => setNotifMessage(e.target.value)} placeholder="Message" rows={3} data-testid="notification-message-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white mb-3 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            <button onClick={sendNotification} disabled={sending} data-testid="notification-send-button" className="bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
              Send to All Users
            </button>
          </div>
        </TabsContent>

        <TabsContent value="jobs" data-testid="admin-content-jobs">
          <div className="glass-panel rounded-2xl p-6 mb-4">
            <div className="flex items-center gap-2 mb-4"><Briefcase className="w-4 h-4 text-indigo-400" /><h3 className="font-heading font-semibold text-white">Post a Job</h3></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={newJob.title} onChange={(e) => setNewJob({ ...newJob, title: e.target.value })} placeholder="Job Title" data-testid="job-title-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              <input value={newJob.company} onChange={(e) => setNewJob({ ...newJob, company: e.target.value })} placeholder="Company" data-testid="job-company-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              <input value={newJob.location} onChange={(e) => setNewJob({ ...newJob, location: e.target.value })} placeholder="Location" data-testid="job-location-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              <input value={newJob.job_type} onChange={(e) => setNewJob({ ...newJob, job_type: e.target.value })} placeholder="Job Type (Full-time)" data-testid="job-type-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              <input value={newJob.apply_url} onChange={(e) => setNewJob({ ...newJob, apply_url: e.target.value })} placeholder="Apply URL" data-testid="job-apply-url-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white sm:col-span-2 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              <textarea value={newJob.description} onChange={(e) => setNewJob({ ...newJob, description: e.target.value })} placeholder="Description" rows={2} data-testid="job-description-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white sm:col-span-2 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
              <select value={newJob.track} onChange={(e) => setNewJob({ ...newJob, track: e.target.value })} data-testid="job-track-select" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white sm:col-span-2 focus:outline-none focus:ring-1 focus:ring-indigo-400">
                <option value="data_cloud">Data Cloud</option>
                <option value="sfmc">SFMC</option>
                <option value="mc_next">MC Next</option>
              </select>
            </div>
            <button onClick={createJob} disabled={creatingJob} data-testid="job-create-button" className="mt-3 flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
              <Plus className="w-4 h-4" /> Post Job
            </button>
          </div>
          <div className="space-y-3">
            {jobs.map((j) => (
              <div key={j.id} className="glass-panel rounded-xl p-4 flex items-center justify-between" data-testid={`admin-job-row-${j.id}`}>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-white">{j.title} — {j.company}</p>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${(TRACK_BADGE[j.track] || TRACK_BADGE.data_cloud).classes}`}>
                      {(TRACK_BADGE[j.track] || TRACK_BADGE.data_cloud).label}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">{j.location} · {j.job_type}</p>
                </div>
                <button onClick={() => deleteJob(j.id)} data-testid={`job-delete-btn-${j.id}`} className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="recruiters" data-testid="admin-content-recruiters">
          <div className="glass-panel rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-white/10">
                  <th className="p-3">Name</th><th className="p-3">Company</th><th className="p-3">Email</th><th className="p-3">Status</th><th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recruiters.map((r) => (
                  <tr key={r.user_id} className="border-b border-white/5 last:border-0" data-testid={`admin-recruiter-row-${r.user_id}`}>
                    <td className="p-3 text-white">{r.name}</td>
                    <td className="p-3 text-slate-300 flex items-center gap-1.5">{r.company_name}</td>
                    <td className="p-3 text-slate-400">{r.email}</td>
                    <td className="p-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${r.recruiter_status === "approved" ? "bg-emerald-400/10 text-emerald-400" : r.recruiter_status === "rejected" ? "bg-red-400/10 text-red-400" : "bg-amber-400/10 text-amber-400"}`}>
                        {r.recruiter_status}
                      </span>
                    </td>
                    <td className="p-3">
                      {r.recruiter_status === "pending" && (
                        <div className="flex items-center gap-2">
                          <button onClick={() => approveRecruiter(r.user_id)} data-testid={`admin-approve-recruiter-btn-${r.user_id}`} className="flex items-center gap-1 text-xs font-semibold text-emerald-400 hover:text-emerald-300">
                            <ShieldCheck className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button onClick={() => rejectRecruiter(r.user_id)} data-testid={`admin-reject-recruiter-btn-${r.user_id}`} className="flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-300">
                            <XCircle className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {recruiters.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-slate-400 text-sm" data-testid="admin-no-recruiters">No recruiter registrations yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="testimonials" data-testid="admin-content-testimonials">
          <div className="space-y-3">
            {testimonials.length === 0 && <p className="text-sm text-slate-400 text-center py-8" data-testid="admin-no-testimonials">No feedback submitted yet.</p>}
            {testimonials.map((t) => (
              <div key={t.id} className="glass-panel rounded-xl p-4" data-testid={`admin-testimonial-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {t.photo ? (
                      <img src={t.photo} alt={t.name} loading="lazy" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-indigo-300">{t.name?.[0]}</div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-white">{t.name} · <span className="text-slate-400">{t.role_title}</span></p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${i < t.rating ? "text-amber-400 fill-amber-400" : "text-slate-600"}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${t.status === "approved" ? "bg-emerald-400/10 text-emerald-400" : t.status === "rejected" ? "bg-red-400/10 text-red-400" : "bg-amber-400/10 text-amber-400"}`}>
                    {t.status}
                  </span>
                </div>
                <p className="text-sm text-slate-300 mt-3">{t.message}</p>
                {t.status === "pending" && (
                  <div className="flex items-center gap-3 mt-3">
                    <button onClick={() => approveTestimonial(t.id)} data-testid={`admin-approve-testimonial-btn-${t.id}`} className="flex items-center gap-1 text-xs font-semibold text-emerald-400 hover:text-emerald-300">
                      <ShieldCheck className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button onClick={() => rejectTestimonial(t.id)} data-testid={`admin-reject-testimonial-btn-${t.id}`} className="flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-300">
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="leads" data-testid="admin-content-leads">
          <div className="flex items-center justify-end mb-3">
            <a href={`${API}/admin/export/leads`} data-testid="export-leads-btn" className="flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors">
              <Download className="w-3.5 h-3.5" /> Export Leads CSV
            </a>
          </div>
          <div className="space-y-3">
            {leads.length === 0 && <p className="text-sm text-slate-400 text-center py-8" data-testid="admin-no-leads">No training inquiries submitted yet.</p>}
            {leads.map((l) => (
              <div key={l.id} className="glass-panel rounded-xl p-4" data-testid={`admin-lead-${l.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-white">{l.name}</p>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${(TRACK_BADGE[l.track] || TRACK_BADGE.data_cloud).classes}`}>
                        {(TRACK_BADGE[l.track] || TRACK_BADGE.data_cloud).label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {l.phone}</span>
                      {l.email && <span>{l.email}</span>}
                      <span>{new Date(l.created_at).toLocaleString()}</span>
                    </p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${
                    l.status === "converted" ? "bg-emerald-400/10 text-emerald-400"
                    : l.status === "contacted" ? "bg-indigo-400/10 text-indigo-400"
                    : l.status === "closed" ? "bg-slate-500/10 text-slate-400"
                    : "bg-amber-400/10 text-amber-400"
                  }`}>
                    {l.status}
                  </span>
                </div>
                {l.message && <p className="text-sm text-slate-300 mt-3">{l.message}</p>}
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  {l.status === "new" && (
                    <button onClick={() => updateLeadStatus(l.id, "contacted")} data-testid={`admin-lead-contacted-btn-${l.id}`} className="flex items-center gap-1 text-xs font-semibold text-indigo-400 hover:text-indigo-300">
                      <PhoneCall className="w-3.5 h-3.5" /> Mark Contacted
                    </button>
                  )}
                  {l.status !== "converted" && (
                    <button onClick={() => updateLeadStatus(l.id, "converted")} data-testid={`admin-lead-converted-btn-${l.id}`} className="flex items-center gap-1 text-xs font-semibold text-emerald-400 hover:text-emerald-300">
                      <ShieldCheck className="w-3.5 h-3.5" /> Mark Converted
                    </button>
                  )}
                  {l.status !== "closed" && (
                    <button onClick={() => updateLeadStatus(l.id, "closed")} data-testid={`admin-lead-closed-btn-${l.id}`} className="flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-300">
                      <XCircle className="w-3.5 h-3.5" /> Close
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="proctoring" data-testid="admin-content-proctoring">
          <ProctoringSessionsPanel />
        </TabsContent>

        <TabsContent value="page-analytics" data-testid="admin-content-page-analytics">
          <PageAnalyticsPanel />
        </TabsContent>

        <TabsContent value="crm" data-testid="admin-content-crm">
          <CrmHubPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
